import React, { useState, useEffect } from 'react';
import { Key, Eye, EyeOff, Copy, ExternalLink, Star, Shield, HardDrive, Edit, Share2, Trash2 } from 'lucide-react';
import { Card } from '../common/Card';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface DigitalAsset {
  id: string;
  name: string;
  type: string;
  url?: string;
  username?: string;
  encrypted_password?: string;
  strength?: 'weak' | 'medium' | 'strong';
  favorite?: boolean;
  description: string;
}

interface DigitalAssetsWidgetProps {
  className?: string;
}

export function DigitalAssetsWidget({ className = '' }: DigitalAssetsWidgetProps) {
  const { user } = useAuth();
  const [assets, setAssets] = useState<DigitalAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  
  useEffect(() => {
    const loadAssets = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('digital_assets')
          .select('id, name, type, url, username, encrypted_password, strength, favorite, description')
          .eq('user_id', user.id)
          .eq('type', 'password')
          .order('favorite', { ascending: false })
          .limit(5);
          
        if (error) throw error;
        setAssets(data || []);
      } catch (error) {
        console.error('Error loading digital assets:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAssets();
  }, [user]);
  
  const togglePasswordVisibility = (id: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };
  
  const getStrengthColor = (strength?: string) => {
    switch (strength) {
      case 'strong':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-amber-100 text-amber-800';
      case 'weak':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'password':
        return <Key size={14} />;
      case 'website':
        return <ExternalLink size={14} />;
      case 'note':
        return <Edit size={14} />;
      case 'share':
        return <Share2 size={14} />;
      case 'delete':
        return <Trash2 size={14} />;
      default:
        return <Key size={14} />;
    }
  };

  const handleViewAsset = (id: string) => {
    // Implementation of handleViewAsset
  };

  const handleEditAsset = (id: string) => {
    // Implementation of handleEditAsset
  };

  const handleShareAsset = (id: string) => {
    // Implementation of handleShareAsset
  };

  const handleDeleteAsset = (id: string) => {
    // Implementation of handleDeleteAsset
  };

  return (
    <Card className={`${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Digital Assets</h3>
          <HardDrive className="h-5 w-5 text-gray-500" />
        </div>
        
        <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1">
          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          ) : assets.length > 0 ? (
            assets.map(asset => (
              <div
                key={asset.id}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-idecide-light group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-full bg-idecide-primary/20 text-idecide-accent">
                    {getAssetIcon(asset.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{asset.name}</p>
                    <p className="text-sm text-gray-500 truncate">{asset.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleViewAsset(asset.id)}
                    className="p-1 text-gray-400 hover:text-idecide-accent opacity-0 group-hover:opacity-100 transition-opacity"
                    title="View details"
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => handleEditAsset(asset.id)}
                    className="p-1 text-gray-400 hover:text-idecide-accent opacity-0 group-hover:opacity-100 transition-opacity relative"
                    title="Edit asset"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => handleShareAsset(asset.id)}
                    className="p-1 text-gray-400 hover:text-idecide-accent opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Share asset"
                  >
                    <Share2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteAsset(asset.id)}
                    className="p-1 text-gray-400 hover:text-idecide-accent opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete asset"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-sm text-gray-500">
              <p>No digital assets found</p>
              <a 
                href="/dashboard/digital/security" 
                className="mt-2 inline-block text-idecide-accent hover:text-idecide-accent-hover"
              >
                Add your first password
              </a>
            </div>
          )}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Shield size={12} />
            <span>All passwords are securely encrypted</span>
          </div>
        </div>
      </div>
    </Card>
  );
}